import { AvailableThemes } from 'fe-lib-theme-types';
import { invariant } from './util/invariant';
import { getState } from './store';
import { warning } from './util/warning';
import { getThemeByName } from './themes';
import type { ThemeValueGetter, ThemeValue } from './themes/types';

/**
 * Extract something out of an object
 *
 * @template T
 * @template Y
 * @param {(t: T) => Y} getter
 * @param {T} obj
 * @return {undefined|Y}
 */
const getIn = (getter, obj) => {
  try {
    return getter(obj);
  } catch (_err) {
    return undefined;
  }
};

/**
 * Return the requested value from inside a theme.
 *
 * The theme is gotten by a global context.
 *
 * @param  {ThemeValueGetter} getter e.g. t => t.typography.body.size
 * @param  {AvailableThemes?} customTheme a theme to use instead of the global one. DO NOT manually pass a value.
 *  Implement a custom theme by using "use" from {@link https://github.hootops.com/hootsuite/fe-global/blob/versioning/platform/fe-lib-theme/README.md|fe-lib-theme}
 * @returns {string} the value in the current theme
 */
export const UNSAFE_getThemeValue = (
  getter: ThemeValueGetter,
  customTheme?: AvailableThemes,
): ThemeValue => {
  invariant(
    typeof getter === 'function',
    'getThemeValue takes a getter. Sample usage: getThemeValue(t => t.colors.primary)',
  );

  let theme = getState();

  if (customTheme) {
    theme = getThemeByName(customTheme);
  }

  const themeValue = getIn(getter, theme) ?? getIn(getter, theme.legacy);

  warning(
    themeValue !== undefined,
    `Cannot find value in theme ${theme.name} using getter ${getter}`,
  );
  return themeValue || '';
};
